import React, {
  useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle
} from 'react';
import Graph from 'graphology';
import Sigma from 'sigma';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import axios from 'axios';
import config from '../config';
import { saveAs } from 'file-saver';
import HelpTip from './HelpTip';
import { useTranslation } from 'react-i18next';

const COUNTRY_NAMES = {
  CZ: 'Czech Republic', CY: 'Cyprus', NL: 'Netherlands',
  DE: 'Germany', AT: 'Austria', SK: 'Slovakia',
};

function truncateLabel(label, max = 20) {
  if (!label || label.length <= max) return label;
  return label.substring(0, max - 1) + '\u2026';
}

function nodeColor(type, insolvent, country, pathPosition, inPath) {
  if (pathPosition === 'start')       return '#38a169';
  if (pathPosition === 'end')         return '#2b6cb0';
  if (insolvent)                       return '#c0392b';
  if (country && country !== 'CZ')    return '#9b59b6';
  if (inPath) return type === 'company' ? '#0694a2' : '#c05621';
  return type === 'company' ? '#3498db' : '#dd6b20';
}

function edgeColor(inPath, active) {
  if (inPath)            return '#e74c3c';
  if (active === false)  return '#d4d4d4';
  return '#cbd5e0';
}

function adaptiveNodeSize(nodeCount) {
  if (nodeCount <= 4)   return 22;
  if (nodeCount <= 10)  return 18;
  if (nodeCount <= 25)  return 15;
  if (nodeCount <= 60)  return 12;
  if (nodeCount <= 150) return 9;
  return 7;
}

function runFA2(graph, iterations = 100) {
  if (graph.order < 2) return;
  try {
    forceAtlas2.assign(graph, {
      iterations,
      settings: {
        gravity: 0.5,
        scalingRatio: 6,
        barnesHutOptimize: graph.order > 200,
      },
    });
  } catch (e) {
    console.warn('[FA2]', e);
  }
}

// ---------------------------------------------------------------
// GraphVisualization — exposes imperative API via forwardRef
// ---------------------------------------------------------------
const GraphVisualization = forwardRef(function GraphVisualization(
  { graphData, pathHighlight, onNodeClick },
  ref
) {
  const { t } = useTranslation();
  const containerRef       = useRef(null);
  const sigmaRef           = useRef(null);
  const graphRef           = useRef(null);
  const onNodeClickRef     = useRef(onNodeClick);
  const pathHighlightRef   = useRef(pathHighlight);
  const showHistoricalRef  = useRef(true);
  const nodeSizeRef        = useRef(14);
  const edgeWidthRef       = useRef(1.5);

  const [nodeSize,       setNodeSize]       = useState(14);
  const [edgeWidth,      setEdgeWidth]      = useState(1.5);
  const [controlsOpen,   setControlsOpen]   = useState(true);
  const [isFullscreen,   setIsFullscreen]   = useState(false);
  const [showHistorical, setShowHistorical] = useState(true);
  const [tooltip,        setTooltip]        = useState({ visible: false, x: 0, y: 0 });
  const [selectedNode,   setSelectedNode]   = useState(null);
  const [expandedNode,   setExpandedNode]   = useState(null);

  // Keep refs in sync with props/state
  onNodeClickRef.current    = onNodeClick;
  pathHighlightRef.current  = pathHighlight;
  showHistoricalRef.current = showHistorical;
  nodeSizeRef.current       = nodeSize;
  edgeWidthRef.current      = edgeWidth;

  // ---------------------------------------------------------------
  // Dimming helper
  // ---------------------------------------------------------------
  const applyDimming = useCallback((g) => {
    const expanded = new Set();
    g.forEachNode((n, a) => { if (a.expanded) expanded.add(n); });

    if (expanded.size === 0) {
      // Restore all
      g.forEachNode((n, a) => {
        g.setNodeAttribute(n, 'color', a.originalColor);
        g.setNodeAttribute(n, 'dimmed', false);
      });
      g.forEachEdge((e, a) => {
        g.setEdgeAttribute(e, 'color', a.originalColor);
        g.setEdgeAttribute(e, 'hidden', a.active === false && !showHistoricalRef.current);
      });
      return;
    }

    const visNodes = new Set();
    const visEdges = new Set();
    expanded.forEach(n => {
      visNodes.add(n);
      g.neighbors(n).forEach(nb => visNodes.add(nb));
      g.edges(n).forEach(e => visEdges.add(e));
    });

    g.forEachNode((n, a) => {
      const dim = !visNodes.has(n);
      const isExp = expanded.has(n);
      g.setNodeAttribute(n, 'color',  dim ? '#e2e8f0' : isExp ? '#f39c12' : a.originalColor);
      g.setNodeAttribute(n, 'dimmed', dim);
    });
    g.forEachEdge((e, a) => {
      const vis = visEdges.has(e);
      g.setEdgeAttribute(e, 'color',  vis ? a.originalColor : '#f0f4f8');
      g.setEdgeAttribute(e, 'hidden', !vis || (a.active === false && !showHistoricalRef.current));
    });
  }, []);

  // ---------------------------------------------------------------
  // Imperative API (used by App.js, SearchBar, ExportControls)
  // ---------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    /** All visible (non-dimmed) node IDs */
    getNodeIds: () => {
      const g = graphRef.current;
      if (!g) return [];
      const ids = [];
      g.forEachNode((n, a) => { if (!a.dimmed) ids.push(n); });
      return ids;
    },

    /** Label of a node by ID */
    getNodeLabel: (id) => {
      const g = graphRef.current;
      if (!g || !g.hasNode(id)) return id;
      return g.getNodeAttribute(id, 'fullLabel') || id;
    },

    /** Add new subgraph nodes+edges, optionally highlight a path */
    addSubgraph: (nodes, edges, pathArray) => {
      const g = graphRef.current;
      const s = sigmaRef.current;
      if (!g || !s) return;

      const pathSet = new Set(pathArray || []);
      const pathPos = {};
      (pathArray || []).forEach((id, i) => {
        pathPos[id] = i === 0 ? 'start' : i === pathArray.length - 1 ? 'end' : 'intermediate';
      });

      nodes.forEach(({ data: nd }) => {
        if (g.hasNode(nd.id)) return;
        const pp  = pathPos[nd.id];
        const inP = pathSet.has(nd.id);
        const col = nodeColor(nd.type, nd.insolvent, nd.country, pp, inP);
        g.addNode(nd.id, {
          label: truncateLabel(nd.label || nd.id),
          fullLabel: nd.label || nd.id,
          size: nodeSizeRef.current,
          color: col, originalColor: col,
          x: Math.random() * 800, y: Math.random() * 600,
          fixed: false,
          in_path: inP, insolvent: nd.insolvent,
          country: nd.country, city: nd.city, nodeClass: nd.type,
          pathPosition: pp, dimmed: false,
        });
      });

      edges.forEach(({ data: ed }) => {
        if (!g.hasNode(ed.source) || !g.hasNode(ed.target)) return;
        if (ed.source === ed.target || g.hasEdge(ed.source, ed.target)) return;
        const inP = pathSet.has(ed.source) && pathSet.has(ed.target);
        const col = edgeColor(inP, ed.active);
        try {
          g.addEdge(ed.source, ed.target, {
            label: ed.type || '',
            size: inP ? edgeWidthRef.current * 2.5 : edgeWidthRef.current,
            color: col, originalColor: col,
            in_path: inP, active: ed.active !== false,
            hidden: ed.active === false && !showHistoricalRef.current,
          });
        } catch (_) {}
      });

      // Update path highlighting for all existing nodes
      if (pathArray && pathArray.length > 0) {
        // First reset any previous path highlighting so old green/blue nodes don't persist
        g.forEachNode((n, a) => {
          if (a.in_path || a.pathPosition) {
            const base = nodeColor(a.nodeClass, a.insolvent, a.country, null, false);
            g.mergeNodeAttributes(n, { in_path: false, pathPosition: null, color: base, originalColor: base });
          }
        });
        g.forEachEdge((e, a) => {
          if (a.in_path) {
            const col = edgeColor(false, a.active);
            g.mergeEdgeAttributes(e, { in_path: false, color: col, originalColor: col, size: edgeWidthRef.current });
          }
        });
        // Then apply new path colors
        g.forEachNode((n, a) => {
          const pp  = pathPos[n];
          const inP = pathSet.has(n);
          if (inP || pp) {
            const col = nodeColor(a.nodeClass, a.insolvent, a.country, pp || null, inP);
            g.mergeNodeAttributes(n, { in_path: inP, pathPosition: pp || null, color: col, originalColor: col });
          }
        });
        g.forEachEdge((e, a, src, tgt) => {
          const inP = pathSet.has(src) && pathSet.has(tgt);
          if (inP) {
            const col = edgeColor(inP, a.active);
            g.mergeEdgeAttributes(e, { in_path: inP, color: col, originalColor: col,
              size: edgeWidthRef.current * 2.5 });
          }
        });
      }

      runFA2(g, 50);
      s.refresh();
    },

    /** PNG export → triggers file download */
    exportPNG: () => {
      const container = containerRef.current;
      if (!container) return;
      try {
        const canvases = Array.from(container.querySelectorAll('canvas'));
        const w = container.clientWidth;
        const h = container.clientHeight;
        const tmp = document.createElement('canvas');
        tmp.width = w; tmp.height = h;
        const ctx = tmp.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        canvases.forEach(c => { if (c.width > 0 && c.height > 0) ctx.drawImage(c, 0, 0); });
        tmp.toBlob(blob => { if (blob) saveAs(blob, 'prepify-graph.png'); });
      } catch (e) { console.error('PNG export error:', e); }
    },

    /** Returns PNG as data URL (for PDF embedding) */
    getPNGDataURL: () => {
      const container = containerRef.current;
      if (!container) return null;
      try {
        const canvases = Array.from(container.querySelectorAll('canvas'));
        const w = container.clientWidth;
        const h = container.clientHeight;
        const tmp = document.createElement('canvas');
        tmp.width = w; tmp.height = h;
        const ctx = tmp.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        canvases.forEach(c => { if (c.width > 0 && c.height > 0) ctx.drawImage(c, 0, 0); });
        return tmp.toDataURL('image/png');
      } catch (e) { return null; }
    },
  }), []);

  // ---------------------------------------------------------------
  // Reactive effects for sliders and toggles
  // ---------------------------------------------------------------
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  useEffect(() => {
    const g = graphRef.current; const s = sigmaRef.current;
    if (!g || !s) return;
    g.forEachNode((n) => g.setNodeAttribute(n, 'size', nodeSize));
    s.refresh();
  }, [nodeSize]);

  useEffect(() => {
    const g = graphRef.current; const s = sigmaRef.current;
    if (!g || !s) return;
    g.forEachEdge((e, a) => g.setEdgeAttribute(e, 'size', a.in_path ? edgeWidth * 2.5 : edgeWidth));
    s.refresh();
  }, [edgeWidth]);

  useEffect(() => {
    const g = graphRef.current; const s = sigmaRef.current;
    if (!g || !s) return;
    g.forEachEdge((e, a) => {
      if (a.active === false) g.setEdgeAttribute(e, 'hidden', !showHistorical);
    });
    s.refresh();
  }, [showHistorical]);

  // ---------------------------------------------------------------
  // Main graph init on graphData change
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!graphData || !containerRef.current) return;

    if (sigmaRef.current) { sigmaRef.current.kill(); sigmaRef.current = null; }

    const g = new Graph({ type: 'undirected', multi: false, allowSelfLoops: false });
    graphRef.current = g;

    const ph     = pathHighlightRef.current;
    const pathIds = ph?.path ?? [];
    const pathSet = new Set(pathIds);
    const pathPos = {};
    pathIds.forEach((id, i) => {
      pathPos[id] = i === 0 ? 'start' : i === pathIds.length - 1 ? 'end' : 'intermediate';
    });

    const W  = containerRef.current.clientWidth  || 800;
    const H  = containerRef.current.clientHeight || 600;
    const cx = W / 2;
    const ch = H / 2;
    const rx = pathIds.length > 1 ? Math.min(W * 0.45, pathIds.length * 90) : 0;
    const ry = pathIds.length > 1 ? Math.min(H * 0.35, pathIds.length * 50) : 0;

    // Add nodes
    (graphData.nodes || []).forEach(({ data }) => {
      if (g.hasNode(data.id)) return;
      const pp  = pathPos[data.id];
      const inP = pathSet.has(data.id);
      const col = nodeColor(data.type, data.insolvent, data.country, pp, inP);
      let x, y, fixed = false;
      if (pp && pathIds.length >= 2) {
        const idx = pathIds.indexOf(data.id);
        const t   = idx / (pathIds.length - 1);
        const ang = Math.PI + t * Math.PI;
        x = cx + rx * Math.cos(ang);
        y = ch + ry * Math.sin(ang) - ry * 0.3;
        fixed = true;
      } else {
        x = cx + (Math.random() - 0.5) * W * 0.7;
        y = ch + (Math.random() - 0.5) * H * 0.7;
      }
      g.addNode(data.id, {
        label: truncateLabel(data.label || data.id),
        fullLabel: data.label || data.id,
        size: nodeSizeRef.current,
        color: col, originalColor: col,
        x, y, fixed,
        in_path: inP, insolvent: data.insolvent,
        country: data.country, city: data.city,
        nodeClass: data.type, pathPosition: pp, dimmed: false,
      });
    });

    // Add edges
    (graphData.edges || []).forEach(({ data }) => {
      if (!g.hasNode(data.source) || !g.hasNode(data.target)) return;
      if (data.source === data.target || g.hasEdge(data.source, data.target)) return;
      const col = edgeColor(data.in_path, data.active);
      try {
        g.addEdge(data.source, data.target, {
          label: data.type || '',
          size: data.in_path ? edgeWidthRef.current * 2.5 : edgeWidthRef.current,
          color: col, originalColor: col,
          in_path: !!data.in_path, active: data.active !== false,
          hidden: data.active === false && !showHistoricalRef.current,
        });
      } catch (_) {}
    });

    // Cluster non-path nodes near their connected path node before layout
    if (pathIds.length >= 2) {
      try {
        g.forEachNode((n, a) => {
          if (a.in_path) return;
          const pathNeighbors = g.neighbors(n).filter(nb => pathSet.has(nb));
          if (pathNeighbors.length > 0) {
            const avgX = pathNeighbors.reduce((s, nb) => s + (g.getNodeAttribute(nb, 'x') || 0), 0) / pathNeighbors.length;
            const avgY = pathNeighbors.reduce((s, nb) => s + (g.getNodeAttribute(nb, 'y') || 0), 0) / pathNeighbors.length;
            if (isFinite(avgX) && isFinite(avgY)) {
              g.setNodeAttribute(n, 'x', avgX + (Math.random() - 0.5) * 140);
              g.setNodeAttribute(n, 'y', avgY + (Math.random() - 0.5) * 140);
            }
          }
        });
      } catch (e) {
        console.warn('[Layout] Clustering error:', e);
      }
    }

    // FA2 layout (path nodes stay fixed via 'fixed' attribute)
    runFA2(g, 100);
    pathIds.forEach(id => { if (g.hasNode(id)) g.setNodeAttribute(id, 'fixed', false); });

    // Sanitize any NaN/Infinity positions produced by FA2 (can diverge on dense subgraphs)
    g.forEachNode((n, a) => {
      if (!isFinite(a.x) || !isFinite(a.y)) {
        g.setNodeAttribute(n, 'x', cx + (Math.random() - 0.5) * 200);
        g.setNodeAttribute(n, 'y', ch + (Math.random() - 0.5) * 200);
      }
    });

    // Adaptive node size based on graph density
    const autoSize = adaptiveNodeSize(g.order);
    nodeSizeRef.current = autoSize;
    setNodeSize(autoSize);
    g.forEachNode((n) => g.setNodeAttribute(n, 'size', autoSize));

    // Create Sigma renderer
    let sigma;
    try {
      sigma = new Sigma(g, containerRef.current, {
        renderEdgeLabels: true,
        defaultEdgeType: 'line',
        minCameraRatio: 0.05,
        maxCameraRatio: 10,
        labelSize: 11,
        labelDensity: 0.07,
        labelGridCellSize: 60,
        labelRenderedSizeThreshold: 5,
        allowInvalidContainer: true,
      });
    } catch (e) {
      console.error('[Sigma] Failed to initialize:', e);
      return;
    }
    sigmaRef.current = sigma;

    // Node dragging — lock bounding box on first interaction so Sigma
    // never auto-rescales the camera when a dragged node moves outside
    // the original graph extent (prevents the "zoom out fast" effect).
    sigma.on('mousedown', () => {
      if (!sigma.getCustomBBox()) sigma.setCustomBBox(sigma.getBBox());
    });

    const dragState = { node: null, dragging: false };

    sigma.on('downNode', ({ node, event }) => {
      if (event.original?.button !== 0) return; // left-click only
      dragState.node = node;
      dragState.dragging = false;
      g.setNodeAttribute(node, 'fixed', true);
    });

    sigma.getMouseCaptor().on('mousemovebody', (e) => {
      if (!dragState.node) return;
      dragState.dragging = true;
      const pos = sigma.viewportToGraph({ x: e.x, y: e.y });
      g.setNodeAttribute(dragState.node, 'x', pos.x);
      g.setNodeAttribute(dragState.node, 'y', pos.y);
      e.preventSigmaDefault();
      e.original.preventDefault();
      e.original.stopPropagation();
    });

    sigma.getMouseCaptor().on('mouseup', () => {
      if (dragState.node && !dragState.dragging) {
        g.setNodeAttribute(dragState.node, 'fixed', false);
      }
      dragState.node = null;
    });

    // Left-click → entity modal (skip if node was just dragged)
    sigma.on('clickNode', ({ node }) => {
      if (dragState.dragging) { dragState.dragging = false; return; }
      const a = g.getNodeAttributes(node);
      const neighbors = g.neighbors(node).map(nb => {
        const nba = g.getNodeAttributes(nb);
        const eid = g.edge(node, nb) || g.edge(nb, node);
        const ea  = eid ? g.getEdgeAttributes(eid) : {};
        return {
          ...nba, id: nb, name: nba.fullLabel, type: nba.nodeClass,
          edgeType: ea.label || 'unknown',
          edgeActive: ea.active !== false,
        };
      });
      if (onNodeClickRef.current) onNodeClickRef.current({ ...a, type: a.nodeClass, id: node, name: a.fullLabel }, neighbors);
    });

    // Hover tooltip
    sigma.on('enterNode', ({ node, event }) => {
      const a = g.getNodeAttributes(node);
      const country = a.country || 'CZ';
      setTooltip({
        visible: true, x: event.x, y: event.y,
        label: a.fullLabel, type: a.nodeClass, nodeId: node,
        city: a.city || '',
        country: COUNTRY_NAMES[country] || country,
        insolvent: a.insolvent === true,
        degree: g.degree(node),
      });
    });
    // eslint-disable-next-line no-unused-expressions
    void t; // satisfy lint — t used in JSX below
    sigma.on('leaveNode', () => setTooltip({ visible: false, x: 0, y: 0 }));

    // Right-click → expand neighbors
    sigma.on('rightClickNode', async ({ node }) => {
      const na = g.getNodeAttributes(node);
      const isExpanded = na.expanded;

      // Load neighbors from API if not yet loaded
      if (!na.neighbors_loaded && !isExpanded) {
        try {
          const resp = await axios.get(`${config.API_BASE_URL}/api/explore/${node}`, {
            params: { existing_nodes: g.nodes().join(',') }
          });
          if (!sigmaRef.current) return; // unmounted during await

          const ePos = g.getNodeAttributes(node);
          resp.data.subgraph.nodes.forEach(({ data: nd }) => {
            if (g.hasNode(nd.id)) return;
            const nc = nodeColor(nd.type, nd.insolvent, nd.country, null, false);
            g.addNode(nd.id, {
              label: truncateLabel(nd.label || nd.id), fullLabel: nd.label || nd.id,
              size: nodeSizeRef.current, color: nc, originalColor: nc,
              x: ePos.x + (Math.random() - 0.5) * 300,
              y: ePos.y + (Math.random() - 0.5) * 300,
              fixed: false, in_path: false, insolvent: nd.insolvent,
              country: nd.country, city: nd.city, nodeClass: nd.type, dimmed: false,
            });
          });
          resp.data.subgraph.edges.forEach(({ data: ed }) => {
            if (!g.hasNode(ed.source) || !g.hasNode(ed.target)) return;
            if (ed.source === ed.target || g.hasEdge(ed.source, ed.target)) return;
            const ec = edgeColor(false, ed.active);
            try {
              g.addEdge(ed.source, ed.target, {
                label: ed.type || '', size: edgeWidthRef.current,
                color: ec, originalColor: ec, in_path: false,
                active: ed.active !== false,
                hidden: ed.active === false && !showHistoricalRef.current,
              });
            } catch (_) {}
          });

          // Settle new nodes: fix existing, run FA2, unfix
          const newIds = new Set(resp.data.subgraph.nodes.map(n => n.data.id));
          g.forEachNode((n) => { if (!newIds.has(n)) g.setNodeAttribute(n, 'fixed', true); });
          runFA2(g, 50);
          g.forEachNode((n) => g.setNodeAttribute(n, 'fixed', false));

          // Re-apply adaptive size after expansion
          const expandedSize = adaptiveNodeSize(g.order);
          nodeSizeRef.current = expandedSize;
          setNodeSize(expandedSize);
          g.forEachNode((n) => g.setNodeAttribute(n, 'size', expandedSize));

          g.setNodeAttribute(node, 'neighbors_loaded', true);
        } catch (err) {
          console.error('Expand error:', err);
          return;
        }
      }

      // Toggle expansion / dimming
      if (isExpanded) {
        g.setNodeAttribute(node, 'expanded', false);
        applyDimming(g);
        if (!g.nodes().some(n => g.getNodeAttribute(n, 'expanded'))) {
          setExpandedNode(null); setSelectedNode(null);
        }
      } else {
        g.setNodeAttribute(node, 'expanded', true);
        applyDimming(g);
        const a = g.getNodeAttributes(node);
        setExpandedNode(node);
        setSelectedNode({ id: node, name: a.fullLabel, type: a.nodeClass, connections: g.degree(node) });
      }
      if (sigmaRef.current) sigmaRef.current.refresh();
    });

    return () => {
      if (sigmaRef.current) { sigmaRef.current.kill(); sigmaRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData]);

  // ---------------------------------------------------------------
  // Control handlers
  // ---------------------------------------------------------------
  const handleZoomIn    = useCallback(() => sigmaRef.current?.getCamera().animatedZoom({ duration: 250 }), []);
  const handleZoomOut   = useCallback(() => sigmaRef.current?.getCamera().animatedUnzoom({ duration: 250 }), []);
  const handleFitScreen = useCallback(() => sigmaRef.current?.getCamera().animatedReset({ duration: 400 }), []);

  const handleResetView = useCallback(() => {
    const g = graphRef.current; const s = sigmaRef.current;
    if (!g || !s) return;
    g.forEachNode((n, a) => {
      g.setNodeAttribute(n, 'expanded', false);
      g.setNodeAttribute(n, 'dimmed', false);
      g.setNodeAttribute(n, 'color', a.originalColor);
    });
    g.forEachEdge((e, a) => {
      g.setEdgeAttribute(e, 'color', a.originalColor);
      g.setEdgeAttribute(e, 'hidden', a.active === false && !showHistoricalRef.current);
    });
    setSelectedNode(null); setExpandedNode(null);
    s.getCamera().animatedReset({ duration: 400 });
    s.refresh();
  }, []);

  const handleResetExpansion = useCallback(() => {
    const g = graphRef.current; const s = sigmaRef.current;
    if (!g || !s) return;
    g.forEachNode((n, a) => {
      g.setNodeAttribute(n, 'expanded', false);
      g.setNodeAttribute(n, 'dimmed', false);
      g.setNodeAttribute(n, 'color', a.originalColor);
    });
    g.forEachEdge((e, a) => {
      g.setEdgeAttribute(e, 'color', a.originalColor);
      g.setEdgeAttribute(e, 'hidden', a.active === false && !showHistoricalRef.current);
    });
    setSelectedNode(null); setExpandedNode(null);
    s.refresh();
  }, []);

  const handleExportPNG = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    try {
      const canvases = Array.from(container.querySelectorAll('canvas'));
      const w = container.clientWidth; const h = container.clientHeight;
      const tmp = document.createElement('canvas');
      tmp.width = w; tmp.height = h;
      const ctx = tmp.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
      canvases.forEach(c => { if (c.width > 0 && c.height > 0) ctx.drawImage(c, 0, 0); });
      tmp.toBlob(blob => { if (blob) saveAs(blob, 'prepify-graph.png'); });
    } catch (e) { console.error('PNG export error:', e); }
  }, []);

  const handleFullscreen = useCallback(() => {
    const el = containerRef.current?.closest('.graph-section') || containerRef.current?.parentElement;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  }, []);

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  if (!graphData) {
    return (
      <div className="graph-container">
        <div className="no-data">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" style={{ marginBottom: 20 }}>
            <circle cx="24" cy="24" r="10" fill="#667eea" opacity="0.6"/>
            <circle cx="56" cy="24" r="7" fill="#764ba2" opacity="0.5"/>
            <circle cx="40" cy="58" r="9" fill="#667eea" opacity="0.55"/>
            <line x1="32" y1="28" x2="50" y2="24" stroke="#cbd5e0" strokeWidth="2"/>
            <line x1="28" y1="32" x2="36" y2="50" stroke="#cbd5e0" strokeWidth="2"/>
            <line x1="52" y1="30" x2="44" y2="50" stroke="#cbd5e0" strokeWidth="2"/>
          </svg>
          <h3>{t('graph.no_data_title')}</h3>
          <p>{t('graph.no_data_subtitle')}</p>
          <div className="no-data-tips">
            <h4>{t('graph.get_started')}</h4>
            <ul>
              <li>{t('graph.tip_1')}</li>
              <li>{t('graph.tip_2')}</li>
              <li>{t('graph.tip_3')}</li>
              <li>{t('graph.tip_4')}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="graph-container">
      <div
        ref={containerRef}
        className="graph-canvas"
        onContextMenu={e => e.preventDefault()}
        style={{ width: '100%', height: isFullscreen ? '100vh' : '600px', position: 'relative' }}
      />

      {/* Controls Panel */}
      <div className={`graph-controls-panel ${controlsOpen ? 'open' : 'collapsed'}`}>
        <button
          className="controls-toggle"
          onClick={() => setControlsOpen(!controlsOpen)}
          title={controlsOpen ? t('graph.collapse_controls') : t('graph.expand_controls')}
        >
          {controlsOpen ? '\u2715' : '\u2699'}
        </button>

        {controlsOpen && (
          <div className="controls-body">
            <div className="controls-buttons">
              <button onClick={handleZoomIn}    title={t('graph.zoom_in')}>+</button>
              <button onClick={handleZoomOut}   title={t('graph.zoom_out')}>&minus;</button>
              <button onClick={handleFitScreen} title={t('graph.fit_screen')}>&#9634;</button>
              <button onClick={handleResetView} title={t('graph.reset_view')}>&#8634;</button>
              <button onClick={handleExportPNG} title={t('graph.export_png')}>&#128247;</button>
              <button onClick={handleFullscreen} title={isFullscreen ? t('graph.exit_fullscreen') : t('graph.fullscreen')}>
                {isFullscreen ? '\u2716' : '\u26F6'}
              </button>
              {expandedNode && (
                <button onClick={handleResetExpansion} title={t('graph.reset_expansion')} className="reset-expansion">
                  &#8617;
                </button>
              )}
            </div>

            <div className="controls-sliders">
              <div className="control-row">
                <label>{t('graph.nodes')}</label>
                <input type="range" min="5" max="30" step="2" value={nodeSize}
                  onChange={e => setNodeSize(Number(e.target.value))} className="control-slider" />
                <span className="control-value">{nodeSize}</span>
              </div>
              <div className="control-row">
                <label>{t('graph.edges')}</label>
                <input type="range" min="0.5" max="5" step="0.5" value={edgeWidth}
                  onChange={e => setEdgeWidth(Number(e.target.value))} className="control-slider" />
                <span className="control-value">{edgeWidth}</span>
              </div>
            </div>

            <div className="controls-toggle-row">
              <label className="toggle-label">
                <span className="toggle-switch-container">
                  <input type="checkbox" checked={showHistorical}
                    onChange={e => setShowHistorical(e.target.checked)} />
                  <span className="toggle-switch-track">
                    <span className="toggle-switch-thumb" />
                  </span>
                </span>
                {t('graph.historical')}
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip.visible && (
        <div className="graph-tooltip" style={{
          position: 'absolute', left: tooltip.x + 10, top: tooltip.y + 10, pointerEvents: 'none'
        }}>
          <strong>{tooltip.label}</strong><br/>
          {t('graph.tooltip_type')} {tooltip.type}<br/>
          {t('graph.tooltip_id')} {tooltip.nodeId}<br/>
          {tooltip.city && <>{t('graph.tooltip_city')} {tooltip.city}<br/></>}
          {t('graph.tooltip_country')} {tooltip.country}<br/>
          {tooltip.insolvent && <span style={{ color: '#fc8181', fontWeight: 'bold' }}>{t('graph.tooltip_insolvent')}<br/></span>}
          {t('graph.tooltip_connections')} {tooltip.degree}
        </div>
      )}

      {/* Selected Node Info */}
      {selectedNode && (
        <div className="selected-node-info">
          <strong>{expandedNode ? t('graph.expanded') : t('graph.selected')}</strong> {selectedNode.name} ({selectedNode.type})
          {selectedNode.connections !== undefined && (
            <span className="connection-count"> &middot; {selectedNode.connections} {t('graph.connections')}</span>
          )}
          {expandedNode && (
            <span className="expand-hint"> &middot; {t('graph.right_click_collapse')}</span>
          )}
        </div>
      )}

      {/* Exploration Hint */}
      {graphData && !expandedNode && (
        <div className="exploration-hint">
          {t('graph.explore_hint')}
        </div>
      )}

      {/* Legend */}
      <div className="graph-legend">
        <div className="legend-item">
          <div className="legend-node company"></div>
          <span>{t('graph.legend_company')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-node person"></div>
          <span>{t('graph.legend_person')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-node insolvent"></div>
          <span>{t('graph.legend_insolvent')} <HelpTip text={t('graph.legend_insolvent_tip')} /></span>
        </div>
        <div className="legend-item">
          <div className="legend-node foreign"></div>
          <span>{t('graph.legend_foreign')} <HelpTip text={t('graph.legend_foreign_tip')} /></span>
        </div>
        <div className="legend-item">
          <div className="legend-node path-start"></div>
          <span>{t('graph.legend_start')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-node path-intermediate"></div>
          <div className="legend-node path-person-intermediate"></div>
          <span>{t('graph.legend_path_node')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-node path-end"></div>
          <span>{t('graph.legend_end')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-edge path"></div>
          <span>{t('graph.legend_path')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-edge inactive"></div>
          <span>{t('graph.legend_historical')} <HelpTip text={t('graph.legend_historical_tip')} /></span>
        </div>
      </div>
    </div>
  );
});

export default GraphVisualization;
